<template>
  <div class="irp-opportunities-tab" data-testid="irp-opportunities-tab">
    <h3 class="irp-opportunities-tab__heading">Opportunity information</h3>
    <IRPFieldGrid
      v-for="(opportunity, index) in ownOpportunities"
      :key="`opportunity_${index}`"
      :fields="opportunity.fields"
    />
    <IRPPartnerFilterBar
      :key="`${individualRecordStore.sourceId}_${individualRecordStore.sourcePrimaryKey}`"
      section="opportunities"
    />
    <p v-if="isLoading" class="irp-opportunities-tab__status">
      Loading opportunities…
    </p>
    <BittsEmptyState
      v-else-if="!partners.length"
      :font-awesome-icon="['fak', 'opportunity']"
      title="No partner opportunities"
      description="No partners share opportunities on this record."
      data-testid="irp-opportunities-empty-state"
    />
    <div v-else class="irp-opportunities-tab__partner-list">
      <IRPPartnerRow
        v-for="partner in partners"
        :key="partner.partner_organization_uuid"
        :partner="partner"
      >
        <IRPFieldGrid
          v-for="(opportunity, index) in partner.opportunities.items"
          :key="`partner_opportunity_${index}`"
          :fields="opportunity.fields"
        />
      </IRPPartnerRow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsEmptyState } from '@crossbeam/bitts';

import { computed, watch } from 'vue';

import { useIndividualRecordStore } from '@/stores';
import type { RecordCollectionType } from '@/types/individual_record';

import IRPFieldGrid from './IRPFieldGrid.vue';
import IRPPartnerFilterBar from './IRPPartnerFilterBar.vue';
import IRPPartnerRow from './IRPPartnerRow.vue';

const individualRecordStore = useIndividualRecordStore();

const ownOpportunities = computed(
  () =>
    (
      individualRecordStore.opportunities?.own?.opportunities as
        | RecordCollectionType
        | undefined
    )?.items ?? [],
);

const partners = computed(
  () => individualRecordStore.opportunities.partners.items,
);
const isLoading = computed(() => individualRecordStore.opportunities.isLoading);

watch(
  () =>
    [
      individualRecordStore.sourceId,
      individualRecordStore.sourcePrimaryKey,
    ] as const,
  () => individualRecordStore.fetchSection('opportunities'),
  { immediate: true },
);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-opportunities-tab {
  @apply flex flex-col gap-16;
}

.irp-opportunities-tab__heading {
  @apply text-base font-bold text-neutral-text-strong;
}

.irp-opportunities-tab__status {
  @apply text-sm text-neutral-text-weak;
}

.irp-opportunities-tab__partner-list {
  @apply flex flex-col;
}
</style>
